/// <amd-module name="Wit/Common/WorkItemTemplateService/IWorkItemTemplateService" />
import { IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { WorkItemTemplate, WorkItemTemplateReference } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { IWorkItemTrackingRestClient } from "Wit/Clients/Wit/RestClient/WorkItemTracking.Types";

export interface IWorkItemTemplateService extends IVssService {
    createTemplate(projectId: string, teamId: string, template: WorkItemTemplate): Promise<WorkItemTemplateReference>;
    getMyTemplateReferences(projectId: string, teamId: string, workItemTypeName: string): Promise<WorkItemTemplateReference[]>;
    getTemplate(projectId: string, teamId: string, templateId: string): Promise<WorkItemTemplate>;
    replaceTemplate(projectId: string, teamId: string, template: WorkItemTemplate): Promise<WorkItemTemplateReference>;
}

const myTemplatesDataProvider = "ms.vss-work-web.work-item-templates-data-provider";

class WorkItemTemplateService extends VssService implements IWorkItemTemplateService {
    private templatesPromiseMap = new Map<string, Promise<WorkItemTemplateReference[] | undefined>>();

    public async createTemplate(projectId: string, teamId: string, template: WorkItemTemplate): Promise<WorkItemTemplateReference> {
        // clear cache so we can load new template
        const key = this.getKey(projectId, template.workItemTypeName);
        this.templatesPromiseMap.delete(key);
        const client = this.pageContext.getRestClient<IWorkItemTrackingRestClient>("IWorkItemTrackingRestClient");
        return await client.createTemplate(template, projectId, teamId);
    }

    public async getTemplate(projectId: string, teamId: string, templateId: string): Promise<WorkItemTemplate> {
        const client = this.pageContext.getRestClient<IWorkItemTrackingRestClient>("IWorkItemTrackingRestClient");
        return client.getTemplate(projectId, teamId, templateId);
    }

    public async getMyTemplateReferences(projectId: string, teamId: string, workItemTypeName: string): Promise<WorkItemTemplateReference[]> {
        const key = this.getKey(projectId, workItemTypeName);
        let templatesPromise = this.templatesPromiseMap.get(key);
        if (!templatesPromise) {
            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
            templatesPromise = contributionService.getDataAsync<WorkItemTemplateReference[]>(
                myTemplatesDataProvider,
                { workItemTypeName },
                /** ignoreCache */ true
            );
            this.templatesPromiseMap.set(key, templatesPromise);
        }

        const templates = await templatesPromise;
        return templates ?? [];
    }

    public async replaceTemplate(projectId: string, teamId: string, template: WorkItemTemplate): Promise<WorkItemTemplate> {
        const key = this.getKey(projectId, template.workItemTypeName);
        this.templatesPromiseMap.delete(key);
        const client = this.pageContext.getRestClient<IWorkItemTrackingRestClient>("IWorkItemTrackingRestClient");
        return await client.replaceTemplate(template, projectId, teamId, template.id);
    }

    private getKey(projectId: string, workItemTypeName: string) {
        return `${projectId}-${workItemTypeName}`;
    }
}

const serviceName = "IWorkItemTemplateService";
Services.add(serviceName, { serviceFactory: WorkItemTemplateService });
